import { useCallback, useEffect, useMemo, useState, type MouseEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  apiLoadErrorText,
  useListMtoReceiptObjectsQuery,
  useListMtoReceiptsQuery,
} from '@shared/api';
import { pluralizeRu } from '@shared/lib/formatFile';
import { Button } from '@shared/ui/Button';
import { Select } from '@shared/ui/Input';
import { SkeletonLine } from '@shared/ui/Skeleton';
import { IconPlus } from '@shared/ui/icons';
import { CheckLabel, Control, FleetChip, Notice, TextButton, typo } from '@entities/fleet';
import {
  MATERIAL_KIND_LABEL,
  MONTHS_FULL,
  formatDate,
  formatMoney,
  formatQty,
  type MaterialKind,
  type Receipt,
  type ReceiptFilter,
} from '@entities/mto/receipts';
import { ReceiptDrawer } from './ReceiptDrawer';
import { EmptyCard, KeyButton, Pager, Table, TableBox, Toolbar, Totals } from './styles';

const PAGE = 100;
const ROWS = ['строка', 'строки', 'строк'] as const;

type Opened = { receipt: Receipt | null } | null;

/**
 * Журнал строк прихода: год, месяц, объект, вид материала, поиск; итоги
 * по всему фильтру над таблицей. Фильтры — в адресе (`?month=&object=&kind=
 * &q=&unmapped=1&issues=1`): из свода по объектам сюда ведёт ссылка.
 */
export function ReceiptsJournal({
  year,
  years,
  canWrite,
  onYear,
  onFlash,
  onImport,
}: {
  year: number;
  years: readonly number[];
  canWrite: boolean;
  onYear: (year: number) => void;
  onFlash: (f: { tone: 'success' | 'danger'; text: string }) => void;
  onImport: () => void;
}) {
  const [params, setParams] = useSearchParams();
  const month = Number(params.get('month')) || undefined;
  const objectId = params.get('object') ?? undefined;
  const kindParam = params.get('kind');
  const kind: MaterialKind | undefined =
    kindParam === 'project' || kindParam === 'other' ? kindParam : undefined;
  const unmapped = params.get('unmapped') === '1';
  const withIssues = params.get('issues') === '1';
  const qParam = params.get('q') ?? '';
  const [query, setQuery] = useState(qParam);
  const [page, setPage] = useState(0);
  const [opened, setOpened] = useState<Opened>(null);

  const setParam = useCallback(
    (patch: Record<string, string | null>) => {
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [k, v] of Object.entries(patch)) {
            if (v === null || v === '') next.delete(k);
            else next.set(k, v);
          }
          return next;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  // Поиск — после паузы в наборе, чтобы не дёргать сервер на каждую букву.
  useEffect(() => {
    const next = query.trim();
    if (next === qParam) return undefined;
    const t = window.setTimeout(() => setParam({ q: next || null }), 400);
    return () => window.clearTimeout(t);
  }, [query, qParam, setParam]);

  useEffect(() => setPage(0), [year, month, objectId, kind, unmapped, withIssues, qParam]);

  const filter: ReceiptFilter = {
    year,
    month,
    object_id: objectId,
    material_kind: kind,
    unmapped: unmapped || undefined,
    with_issues: withIssues || undefined,
    q: qParam || undefined,
    limit: PAGE,
    offset: page * PAGE,
  };
  const { data, isLoading, isFetching, error, refetch } = useListMtoReceiptsQuery(filter);
  const { data: objectsData } = useListMtoReceiptObjectsQuery({ year });
  const objectOptions = useMemo(
    () =>
      (objectsData?.items ?? [])
        .filter((o) => o.rows_year > 0 || o.id === objectId)
        .sort((a, b) => a.name.localeCompare(b.name, 'ru')),
    [objectsData, objectId],
  );
  const filtered =
    month !== undefined ||
    objectId !== undefined ||
    kind !== undefined ||
    unmapped ||
    withIssues ||
    qParam !== '';

  const yearOptions = years.includes(year) ? years : [year, ...years];
  const items = data?.items ?? [];
  const total = data?.total ?? 0;

  const onRow = (r: Receipt) => (e: MouseEvent<HTMLTableRowElement>) => {
    if (e.target instanceof HTMLElement && e.target.closest('button')) return;
    if (window.getSelection()?.toString()) return;
    setOpened({ receipt: r });
  };

  const addButton = canWrite && (
    <Button type="button" $variant="primary" onClick={() => setOpened({ receipt: null })}>
      <IconPlus size={16} />
      Добавить строку
    </Button>
  );

  return (
    <>
      <Toolbar>
        <Select
          aria-label="Год"
          value={String(year)}
          onChange={(e) => onYear(Number(e.target.value))}
        >
          {yearOptions.map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </Select>
        <Select
          aria-label="Месяц"
          value={month ? String(month) : ''}
          onChange={(e) => setParam({ month: e.target.value || null })}
        >
          <option value="">Весь год</option>
          {MONTHS_FULL.map((m, i) => (
            <option key={m} value={i + 1}>
              {m[0]?.toUpperCase()}
              {m.slice(1)}
            </option>
          ))}
        </Select>
        <Select
          aria-label="Объект"
          value={objectId ?? ''}
          onChange={(e) => setParam({ object: e.target.value || null })}
        >
          <option value="">Все объекты</option>
          {objectOptions.map((o) => (
            <option key={o.id} value={o.id}>
              {o.name}
            </option>
          ))}
        </Select>
        <Select
          aria-label="Вид материала"
          value={kind ?? ''}
          onChange={(e) => setParam({ kind: e.target.value || null })}
        >
          <option value="">Все материалы</option>
          <option value="project">{MATERIAL_KIND_LABEL.project}</option>
          <option value="other">{MATERIAL_KIND_LABEL.other}</option>
        </Select>
        <Control
          className="search"
          type="search"
          value={query}
          placeholder="Поставщик, материал, № счёта-фактуры"
          aria-label="Поиск по приходу"
          onChange={(e) => setQuery(e.target.value)}
        />
        <CheckLabel>
          <input
            type="checkbox"
            checked={unmapped}
            onChange={(e) => setParam({ unmapped: e.target.checked ? '1' : null })}
          />
          Объект не сопоставлен
        </CheckLabel>
        <CheckLabel>
          <input
            type="checkbox"
            checked={withIssues}
            onChange={(e) => setParam({ issues: e.target.checked ? '1' : null })}
          />
          С замечаниями
        </CheckLabel>
        <span className="spacer" />
        {items.length > 0 && addButton}
      </Toolbar>

      {data && total > 0 && (
        <Totals aria-live="polite">
          <span>
            <b>{total.toLocaleString('ru-RU')}</b> {pluralizeRu(total, ROWS)}
          </span>
          <span>
            Без НДС: <b>{formatMoney(data.amount)}</b>
          </span>
          <span>
            НДС: <b>{formatMoney(data.vat_amount)}</b>
          </span>
          <span>
            С НДС: <b>{formatMoney(data.amount_with_vat)}</b>
          </span>
        </Totals>
      )}

      {isLoading ? (
        <div aria-busy="true" aria-label="Загрузка прихода">
          <SkeletonLine $height={40} />
          {Array.from({ length: 6 }, (_, i) => (
            <SkeletonLine key={i} $height={20} $width={i % 2 === 0 ? '100%' : '70%'} />
          ))}
        </div>
      ) : error !== undefined && !data ? (
        <Notice $tone="danger" role="alert">
          <span className="text">
            {apiLoadErrorText(error, 'Приход не загрузился.', 'Нет доступа к приходу ТМЦ.')}
          </span>
          <TextButton type="button" disabled={isFetching} onClick={() => void refetch()}>
            Повторить
          </TextButton>
        </Notice>
      ) : total === 0 && !filtered ? (
        <EmptyCard>
          <h3>Прихода за {year} год нет</h3>
          <p>
            {typo(
              canWrite
                ? 'Загрузите книгу Excel «Приход ТМЦ» целиком — все годы сразу, или внесите строку вручную.'
                : 'Приход ведёт отдел МТО: как только книгу загрузят или внесут строки, они появятся здесь.',
            )}
          </p>
          {canWrite && (
            <div className="actions">
              <Button type="button" $variant="primary" onClick={onImport}>
                Загрузить книгу Excel
              </Button>
              {addButton}
            </div>
          )}
        </EmptyCard>
      ) : total === 0 ? (
        <EmptyCard>
          <h3>Ничего не нашлось</h3>
          <p>{typo('По этим условиям строк прихода нет — смягчите фильтр.')}</p>
          <div className="actions">
            <Button
              type="button"
              $variant="secondary"
              onClick={() => {
                setQuery('');
                setParam({
                  month: null,
                  object: null,
                  kind: null,
                  unmapped: null,
                  issues: null,
                  q: null,
                });
              }}
            >
              Сбросить фильтры
            </Button>
          </div>
        </EmptyCard>
      ) : (
        <>
          <TableBox>
            <Table>
              <thead>
                <tr>
                  <th scope="col">Дата</th>
                  <th scope="col">Поставщик</th>
                  <th scope="col">Материал</th>
                  <th scope="col" className="num">
                    Кол-во
                  </th>
                  <th scope="col" className="num">
                    Без НДС
                  </th>
                  <th scope="col" className="num">
                    С НДС
                  </th>
                  <th scope="col">Объект</th>
                  <th scope="col">Принял</th>
                </tr>
              </thead>
              <tbody>
                {items.map((r) => (
                  <tr key={r.id} data-open="true" onClick={onRow(r)}>
                    <td className="nowrap">
                      <KeyButton type="button" onClick={() => setOpened({ receipt: r })}>
                        {formatDate(r.receipt_date)}
                      </KeyButton>
                    </td>
                    <td>
                      {r.supplier}
                      {r.invoice_no && <div className="sub">с/ф {r.invoice_no}</div>}
                    </td>
                    <td>
                      {r.item_name}
                      {r.issues.length > 0 && <div className="warn">{r.issues.join('; ')}</div>}
                    </td>
                    <td className="num">
                      {formatQty(r.quantity)}
                      {r.unit && <div className="sub">{r.unit}</div>}
                    </td>
                    <td className="num">{formatMoney(r.amount)}</td>
                    <td className="num">
                      {formatMoney(r.amount_with_vat)}
                      {r.vat_free && <div className="sub">без НДС</div>}
                    </td>
                    <td>
                      {r.object_name ?? '—'}
                      <div className="sub">
                        {r.object_target ? (
                          <>
                            → {r.object_target}
                            {r.material_kind &&
                              ` · ${MATERIAL_KIND_LABEL[r.material_kind].toLowerCase()}`}
                          </>
                        ) : (
                          <FleetChip $tone="warning">не сопоставлен</FleetChip>
                        )}
                      </div>
                    </td>
                    <td>{r.signed_by ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableBox>
          {total > PAGE && (
            <Pager>
              <span>
                {page * PAGE + 1}–{Math.min((page + 1) * PAGE, total)} из{' '}
                {total.toLocaleString('ru-RU')}
              </span>
              <Button
                type="button"
                $variant="ghost"
                $size="sm"
                disabled={page === 0 || isFetching}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
              >
                Назад
              </Button>
              <Button
                type="button"
                $variant="ghost"
                $size="sm"
                disabled={(page + 1) * PAGE >= total || isFetching}
                onClick={() => setPage((p) => p + 1)}
              >
                Дальше
              </Button>
            </Pager>
          )}
        </>
      )}

      {opened && (
        <ReceiptDrawer
          key={opened.receipt?.id ?? 'new'}
          receipt={opened.receipt}
          canWrite={canWrite}
          objectNames={(objectsData?.items ?? []).map((o) => o.name)}
          onClose={() => setOpened(null)}
          onSaved={(text) => {
            setOpened(null);
            onFlash({ tone: 'success', text });
          }}
        />
      )}
    </>
  );
}
